import { Fragment, type MouseEvent, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/icon';

export interface AgendaBreadcrumbItem {
  icon?: ReactNode;
  label: ReactNode;
  /** Compact identifier shown right-aligned next to the label (e.g. `#218`). */
  id?: ReactNode;
  /** Subtitle shown after the label for the current node. */
  sub?: ReactNode;
  current?: boolean;
  onClick?: (event: MouseEvent<HTMLSpanElement>) => void;
}

export interface AgendaBreadcrumbProps {
  items: readonly AgendaBreadcrumbItem[];
  className?: string;
}

/**
 * Rich breadcrumb used inside the `AgendaWindow` header.  Differs from the
 * generic `Breadcrumb` by supporting icons, ID badges and a subtitle on the
 * currently-selected step.
 */
export function AgendaBreadcrumb({ items, className }: AgendaBreadcrumbProps) {
  return (
    <nav
      aria-label="breadcrumb"
      className={cn(
        'phx-aw-bc text-phx-md text-phx-text gap-phx-2_5 flex min-w-0 flex-1 items-center overflow-hidden',
        className
      )}
    >
      {items.map((item, index) => (
        <Fragment key={index}>
          {index > 0 ? (
            <span className="phx-aw-bc-sep text-phx-text-faint inline-flex shrink-0">
              <Icon name="chevright" size={11} />
            </span>
          ) : null}
          <span
            data-current={item.current ? '' : undefined}
            onClick={item.onClick}
            className={cn(
              'phx-aw-bc-item gap-phx-3 inline-flex min-w-0 items-baseline whitespace-nowrap',
              'rounded-phx-sm text-phx-text-2 px-phx-2 py-phx-1 font-medium transition-colors',
              item.onClick && 'hover:bg-phx-surface-2 hover:text-phx-text cursor-pointer',
              item.current && 'text-phx-text overflow-hidden font-semibold'
            )}
          >
            {item.icon ? (
              <span className="phx-aw-bc-icon text-phx-lg inline-flex items-center self-center leading-none">
                {item.icon}
              </span>
            ) : null}
            <span className="phx-aw-bc-label min-w-0 truncate">{item.label}</span>
            {item.id ? (
              <span className="phx-aw-bc-id text-phx-xs text-phx-text-muted shrink-0 font-mono font-medium">
                {item.id}
              </span>
            ) : null}
            {item.sub ? (
              <span className="phx-aw-bc-sub text-phx-sm text-phx-text-muted shrink-0 font-normal">
                {item.sub}
              </span>
            ) : null}
          </span>
        </Fragment>
      ))}
    </nav>
  );
}
